// MBA Lab — Peer Evaluation Drafter UI. Imports only lib/core.js,
// lib/peer-eval-model.js, lib/peer-eval-checks.js, lib/peer-eval-prompts.js
// and lib/tool-dock.js.
//
// Session-only persistence, full stop - the same posture as the listening
// session facilitator, and for a stronger version of its reason. Every field
// here is a candid judgment about a named classmate, and the five calibration
// boxes are attestations about one specific piece of text ("would I say this to
// them, in these words?") that mean nothing at all once that text is stripped.
// So there is no opt-in "remember on this device" tier: this autosaves to
// sessionStorage for the current tab, and is gone when the tab closes.
//
// The teammate label never leaves this file's own DOM. It is not in any
// analytics call, and the only params this tool sends are two integers.
import { sessionStore, download, trackToolAction } from './lib/core.js';
import {
  createInitialState,
  parseSavedState,
  serializeForSave,
  addEvaluation,
  removeEvaluation,
  selectEvaluation,
  getActiveEvaluation,
  setTeammateLabel,
  setEvaluationField,
  setRaised,
  setCalibration,
  sectionsStartedFor,
  maxSectionsStarted,
  isEvaluationReady,
  hasAnyContent,
  buildPlainText,
  MAX_EVALUATIONS,
  SECTION_COUNT,
} from './lib/peer-eval-model.js';
import { checkIdsFor, runFieldChecks } from './lib/peer-eval-checks.js';
import { initToolDock } from './lib/tool-dock.js';

const TOOL_NAME = 'peer-evaluation-drafter';
const AUTOSAVE_DEBOUNCE_MS = 400;
const RESULT_SECTION_THRESHOLD = 3;

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el && el.firstChild) el.removeChild(el.firstChild);
}

class PeerEvaluationDrafter {
  constructor() {
    this.cacheElements();
    this.sessionStore = sessionStore(TOOL_NAME);
    this.saveTimer = null;
    this.hasEngaged = false;
    this.hasReported = false;

    this.dock = initToolDock({
      dock: byId('peer-eval-dock'),
      jumpTarget: byId('peer-eval-draft'),
      // The whole panel, not #peer-eval-editor - the editor is hidden until a
      // first teammate exists, and "Back to form" has to work before then.
      returnTarget: byId('peer-eval-form'),
    });

    this.state = this.loadInitialState();
    this.wireEvents();
    this.renderAll();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.root = byId('peer-eval-app');
    this.els = {
      tabs: byId('peer-eval-tabs'),
      addBtn: byId('peer-eval-add'),
      limitNote: byId('peer-eval-limit-note'),
      empty: byId('peer-eval-empty'),
      editor: byId('peer-eval-editor'),
      removeBtn: byId('peer-eval-remove'),
      label: byId('peer-eval-label'),
      raisedNoteWrap: byId('peer-eval-raised-note-wrap'),
      readyNote: byId('peer-eval-ready-note'),
      progress: byId('peer-eval-progress'),
      draftText: byId('peer-eval-draft-text'),
      copyBtn: byId('peer-eval-copy'),
      downloadBtn: byId('peer-eval-download'),
      clearBtn: byId('peer-eval-clear'),
      actionFeedback: byId('peer-eval-action-feedback'),
    };
    this.fieldEls = Array.from(this.root.querySelectorAll('[data-peer-field]'));
    this.checkLists = Array.from(this.root.querySelectorAll('[data-peer-checks]'));
    this.raisedInputs = Array.from(this.root.querySelectorAll('[data-peer-raised]'));
    this.calibrationInputs = Array.from(this.root.querySelectorAll('[data-peer-calibration]'));
  }

  loadInitialState() {
    const saved = this.sessionStore.load();
    if (!saved) return createInitialState();
    const result = parseSavedState(saved);
    if (result.status === 'ok' || result.status === 'migrated') return result.state;
    return createInitialState();
  }

  // --- events -------------------------------------------------------------

  wireEvents() {
    this.els.addBtn.addEventListener('click', () => this.handleAdd());
    this.els.removeBtn.addEventListener('click', () => this.handleRemove());
    this.els.clearBtn.addEventListener('click', () => this.handleClear());
    this.els.copyBtn.addEventListener('click', () => this.handleCopy());
    this.els.downloadBtn.addEventListener('click', () => this.handleDownload());

    this.els.label.addEventListener('input', () => {
      this.state = setTeammateLabel(this.state, this.state.activeIndex, this.els.label.value);
      this.afterEdit();
    });

    for (const el of this.fieldEls) {
      el.addEventListener('input', () => {
        this.state = setEvaluationField(this.state, this.state.activeIndex, el.dataset.peerField, el.value);
        this.renderChecksFor(el.dataset.peerField);
        this.afterEdit();
      });
    }

    for (const input of this.raisedInputs) {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        this.state = setRaised(this.state, this.state.activeIndex, input.dataset.peerRaised);
        this.applyActiveEvaluationToForm();
        this.afterEdit();
      });
    }

    for (const input of this.calibrationInputs) {
      input.addEventListener('change', () => {
        this.state = setCalibration(this.state, this.state.activeIndex, input.dataset.peerCalibration, input.checked);
        this.afterEdit();
      });
    }

    // Same two listeners, same reason, as listening-session-facilitator.js: a
    // plain debounce loses up to AUTOSAVE_DEBOUNCE_MS of just-typed text if the
    // tab goes away inside that window. visibilitychange covers tab-switch and
    // most navigation; pagehide is the iOS Safari backstop.
    document.addEventListener('visibilitychange', () => this.flushIfHidden());
    window.addEventListener('pagehide', () => this.flushPendingAutosave());
  }

  flushIfHidden() {
    if (document.visibilityState === 'hidden') this.flushPendingAutosave();
  }

  flushPendingAutosave() {
    if (!this.saveTimer) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
    this.autosave();
  }

  handleAdd() {
    if (this.state.evaluations.length >= MAX_EVALUATIONS) return;
    this.state = addEvaluation(this.state);
    this.markEngaged();
    this.renderAll();
    this.scheduleAutosave();
    this.els.label.focus();
  }

  handleRemove() {
    const index = this.state.activeIndex;
    if (index < 0) return;
    this.state = removeEvaluation(this.state, index);
    this.renderAll();
    this.scheduleAutosave();
    this.showActionFeedback('Removed.');
  }

  /** renderTabs() rebuilds the strip's buttons, which drops focus to <body> if
   * the tab the user just activated by keyboard was the focused element. Carry
   * it across to the tab that is now selected - only when focus was already in
   * the strip, so a mouse click doesn't steal it from wherever it was. */
  handleSelect(index) {
    const hadFocusInTabs = this.els.tabs.contains(document.activeElement);
    this.state = selectEvaluation(this.state, index);
    this.renderAll();
    this.scheduleAutosave();
    if (!hadFocusInTabs) return;
    const activeTab = this.els.tabs.querySelector(`[data-peer-tab="${this.state.activeIndex}"]`);
    if (activeTab) activeTab.focus();
  }

  /** WAI-ARIA APG tablist keyboard pattern, matching the journal's tabs. */
  handleTabKeydown(e, index) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const count = this.state.evaluations.length;
    if (!count) return;

    let next;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = count - 1;
    else if (e.key === 'ArrowLeft') next = (index - 1 + count) % count;
    else next = (index + 1) % count;

    // handleSelect carries focus onto the newly selected tab for us, because
    // focus is inside the strip at this point by definition.
    this.handleSelect(next);
  }

  handleClear() {
    this.sessionStore.clear();
    this.state = createInitialState();
    this.renderAll();
    this.showActionFeedback('Cleared.');
  }

  /** Every edit re-renders the tab strip: a tab's visible name comes from the
   * label field, and its is-ready state can flip on any field, so anything less
   * than "always" leaves one of the two stale. Eight buttons at most, so the
   * cheap correct thing beats the fiddly fast one. Nothing inside the strip
   * holds focus while a field is being edited, and the keyboard handler
   * re-focuses explicitly for the case where it does. */
  afterEdit() {
    this.markEngaged();
    this.renderTabs();
    this.renderProgress();
    this.renderDraft();
    this.maybeReportResult();
    this.scheduleAutosave();
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
  }

  /**
   * Fires once per page, when any single evaluation has reached 3 of its 5
   * sections - the point at which someone has genuinely drafted something
   * rather than opened the page and typed a name. Two integers only: how many
   * tabs exist, and how far the furthest one got. No label, no field text.
   */
  maybeReportResult() {
    if (this.hasReported) return;
    const best = maxSectionsStarted(this.state);
    if (best < RESULT_SECTION_THRESHOLD) return;
    this.hasReported = true;
    // result_v2, not result: tool_result is at GA4's param-count ceiling - see analytics-events.js.
    trackToolAction(TOOL_NAME, 'result_v2', {
      evaluation_count: this.state.evaluations.length,
      sections_started: best,
    });
  }

  // --- render -------------------------------------------------------------

  renderAll() {
    this.renderTabs();
    this.applyActiveEvaluationToForm();
    this.renderProgress();
    this.renderDraft();
  }

  renderTabs() {
    clearChildren(this.els.tabs);
    this.state.evaluations.forEach((evaluation, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.id = `peer-eval-tab-${index}`;
      tab.className = 'tool-peer-tab' + (index === this.state.activeIndex ? ' is-active' : '');
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', index === this.state.activeIndex ? 'true' : 'false');
      tab.setAttribute('aria-controls', 'peer-eval-editor');
      // Roving tabindex: Tab reaches the strip once and lands on the selected
      // teammate, then the arrow keys move between them (WAI-ARIA APG). All
      // tabs share the one editor panel, so aria-controls is the same for each.
      tab.tabIndex = index === this.state.activeIndex ? 0 : -1;
      tab.dataset.peerTab = String(index);
      tab.textContent = evaluation.teammateLabel.trim() || `Teammate ${index + 1}`;
      if (isEvaluationReady(evaluation)) tab.classList.add('is-ready');
      tab.addEventListener('click', () => this.handleSelect(index));
      tab.addEventListener('keydown', (e) => this.handleTabKeydown(e, index));
      this.els.tabs.appendChild(tab);
    });

    const atLimit = this.state.evaluations.length >= MAX_EVALUATIONS;
    this.els.addBtn.disabled = atLimit;
    this.els.limitNote.hidden = !atLimit;
  }

  applyActiveEvaluationToForm() {
    const evaluation = getActiveEvaluation(this.state);
    const hasActive = !!evaluation;

    this.els.empty.hidden = hasActive;
    this.els.editor.hidden = !hasActive;
    if (hasActive) {
      this.els.editor.setAttribute('aria-labelledby', `peer-eval-tab-${this.state.activeIndex}`);
    }

    this.els.label.value = evaluation ? evaluation.teammateLabel : '';
    for (const el of this.fieldEls) el.value = evaluation ? evaluation[el.dataset.peerField] ?? '' : '';
    for (const input of this.raisedInputs) {
      input.checked = !!evaluation && evaluation.raised === input.dataset.peerRaised;
    }
    for (const input of this.calibrationInputs) {
      input.checked = !!evaluation && evaluation.calibration[input.dataset.peerCalibration] === true;
    }

    this.els.raisedNoteWrap.hidden = !evaluation || evaluation.raised !== 'no';
    for (const list of this.checkLists) this.renderChecksFor(list.dataset.peerChecks);
  }

  renderChecksFor(field) {
    const list = this.checkLists.find((el) => el.dataset.peerChecks === field);
    if (!list) return;
    const evaluation = getActiveEvaluation(this.state);
    clearChildren(list);
    // Per-field passes only - no aggregate, no score. See peer-eval-checks.js.
    for (const check of runFieldChecks(evaluation ? evaluation[field] : '', checkIdsFor(field))) {
      const li = document.createElement('li');
      li.className = check.passed ? 'tool-peer-check is-pass' : 'tool-peer-check';
      li.textContent = check.label;
      list.appendChild(li);
    }
  }

  renderProgress() {
    const evaluation = getActiveEvaluation(this.state);
    const started = sectionsStartedFor(evaluation);
    const count = this.state.evaluations.length;
    const reading = count
      ? `${started} of ${SECTION_COUNT} sections started`
      : 'No teammates added yet';

    this.els.progress.textContent = reading;
    this.els.readyNote.hidden = !isEvaluationReady(evaluation);
    this.dock?.setReading(reading);
    this.dock?.setComplete(isEvaluationReady(evaluation));

    const has = hasAnyContent(this.state);
    this.els.copyBtn.disabled = !has;
    this.els.downloadBtn.disabled = !has;
  }

  renderDraft() {
    this.els.draftText.textContent = buildPlainText(this.state);
  }

  // --- persistence and output ---------------------------------------------

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    this.saveTimer = null;
    const toSave = { ...this.state, savedAt: Date.now() };
    if (!serializeForSave(toSave)) return;
    this.sessionStore.save(toSave);
  }

  async handleCopy() {
    const text = buildPlainText(this.state);
    if (!hasAnyContent(this.state)) return;

    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) copied = this.fallbackCopy(text);

    this.showActionFeedback(copied ? 'Copied!' : 'Could not copy - try selecting the text manually.');
    if (copied) trackToolAction(TOOL_NAME, 'copy');
  }

  handleDownload() {
    if (!hasAnyContent(this.state)) return;
    // .txt, not .md - the destination is a plain textarea on a course form.
    download('peer-evaluation-notes.txt', 'text/plain;charset=utf-8', buildPlainText(this.state));
    trackToolAction(TOOL_NAME, 'download');
    this.showActionFeedback('Downloaded.');
  }

  fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  }

  showActionFeedback(message) {
    this.els.actionFeedback.textContent = message;
    setTimeout(() => {
      if (this.els.actionFeedback.textContent === message) this.els.actionFeedback.textContent = '';
    }, 3000);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!byId('peer-eval-app')) return;
  new PeerEvaluationDrafter();
});
